import { bezSierotek } from '@insolitum/ui';
import { Mail, ArrowLeft, AlertCircle, Check } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { IKONA_SUKCESU, POLE_WEJSCIA, UkladWejscia } from '../../components/marka/UkladWejscia';
import { supabase } from '../../lib/supabase';

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (resetError) throw resetError;
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || t('errors.somethingWentWrong'));
    } finally {
      setIsLoading(false);
    }
  };

  // Stan sukcesu w tym samym układzie co formularz: znak i przełącznik
  // języka zostają na miejscu, zmienia się tylko treść pod nimi.
  if (success) {
    return (
      <UkladWejscia>
        <div className="card rounded-ids-2xl shadow-elev-4 p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/20 mb-4">
            <Check className={`w-8 h-8 ${IKONA_SUKCESU}`} />
          </div>
          <h1 className="ids-tytul-ekranu">{t('auth.checkEmail')}</h1>
          <p className="ids-tresc-ekranu mx-auto">
            {t('auth.resetLinkSent')} <strong className="text-aurora-text">{email}</strong>
          </p>
          <Link to="/login" className="btn btn--primary btn--lg mt-8">
            <ArrowLeft className="w-4 h-4" />
            {t('auth.backToLogin')}
          </Link>
        </div>
      </UkladWejscia>
    );
  }

  return (
    <UkladWejscia>
      <div className="text-center">
        <h1 className="ids-tytul-ekranu">{t('auth.resetPassword')}</h1>
        <p className="ids-tresc-ekranu mx-auto">{bezSierotek(t('auth.resetPasswordDesc'))}</p>
      </div>

      {/* Reset Form */}
      <div className="card rounded-ids-2xl shadow-elev-4 p-8 mt-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-ids-md text-red-400 [[data-mode=light]_&]:text-red-800 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {bezSierotek(error)}
            </div>
          )}

          <div className="field">
            <label htmlFor="email" className="field__label">
              {t('auth.email')}
            </label>
            <div className="inputwrap">
              <Mail />
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className={`input ${POLE_WEJSCIA}`}
                placeholder={t('auth.emailPlaceholder')}
              />
            </div>
          </div>

          <button type="submit" disabled={isLoading} className="btn btn--primary btn--block">
            {isLoading ? (
              <span className="spinner" />
            ) : (
              <>
                <Mail className="w-5 h-5" />
                {t('auth.sendResetLink')}
              </>
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-aurora-muted hover:text-aurora-text text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('auth.backToLogin')}
          </Link>
        </div>
      </div>
    </UkladWejscia>
  );
}
